<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    width?: string
  }>(),
  { width: 'max-w-lg' },
)
const mounted = useMountedWhileOpen(() => props.open)

const emit = defineEmits<{ close: [] }>()
</script>

<template>
  <TransitionRoot v-if="mounted" appear :show="open" as="template">
    <Dialog class="relative z-50" @close="emit('close')">
      <!-- Overlay -->
      <TransitionChild
        enter="transition-opacity duration-300" enter-from="pointer-events-none opacity-0" enter-to="opacity-100"
        leave="transition-opacity duration-200" leave-from="opacity-100" leave-to="pointer-events-none opacity-0"
      >
        <div class="fixed inset-0 bg-black/20 dark:bg-black/60" />
      </TransitionChild>

      <div class="pointer-events-none fixed inset-0 flex justify-end">
        <TransitionChild
          enter="transition-transform duration-300 ease-out" enter-from="translate-x-full" enter-to="translate-x-0"
          leave="transition-transform duration-200 ease-in" leave-from="translate-x-0" leave-to="translate-x-full"
        >
          <DialogPanel
            class="pointer-events-auto flex h-full w-screen flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-xl"
            :class="width"
          >
            <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <DialogTitle class="text-sm font-semibold text-gray-900">{{ title }}</DialogTitle>
              <button
                class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                @click="emit('close')"
              >
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>

            <div class="flex-1 px-5 py-4">
              <slot />
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
